import { useEffect, useMemo, useState } from "react";
import Plot from "react-plotly.js";
import { fetchHeatmapData } from "../api/postgrest";
import { ThermoDetail, ThermoHeightReferenceLine, ThermoImage, ThermoPixel } from "../types";
import { toAppFileUrl } from "../utils/fileUrls";
import { ThermoHeightCalibration } from "../utils/thermoHeightProjection";
import { generateTempColorscale } from "../utils/colormap";

type PlotShape = {
  x0?: number;
  y0?: number;
  x1?: number;
  y1?: number;
  type?: string;
  line?: { color?: string; width?: number };
};

type Props = {
  open: boolean;
  images: ThermoImage[];
  details: Record<number, ThermoDetail>;
  nasMountPath?: string;
  onClose: () => void;
  onApply: (calibration: ThermoHeightCalibration) => Promise<void> | void;
};

function shuffle<T>(items: T[]) {
  const next = [...items];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }
  return next;
}

function buildTempImagePath(nasMountPath: string | undefined, image: ThermoImage) {
  if (!nasMountPath) return "";
  const cleanedMount = nasMountPath.replace(/\\/g, "/").replace(/\/$/, "");
  return toAppFileUrl(`${cleanedMount}/thermoimaging/output/${image.id}/temp_image.png`);
}

function extractLineShape(shapes: PlotShape[]): ThermoHeightReferenceLine | null {
  const valid = [...shapes]
    .reverse()
    .find(
      (shape) =>
        Number.isFinite(shape.x0) &&
        Number.isFinite(shape.y0) &&
        Number.isFinite(shape.x1) &&
        Number.isFinite(shape.y1)
    );
  if (!valid) return null;
  return {
    x0: Number(valid.x0),
    y0: Number(valid.y0),
    x1: Number(valid.x1),
    y1: Number(valid.y1)
  };
}

function clampShape(shape: ThermoHeightReferenceLine, width: number, height: number): ThermoHeightReferenceLine {
  const clampX = (value: number) => Math.max(0, Math.min(Math.max(0, width - 1), Math.round(value)));
  const clampY = (value: number) => Math.max(0, Math.min(Math.max(0, height - 1), Math.round(value)));
  return {
    x0: clampX(shape.x0),
    y0: clampY(shape.y0),
    x1: clampX(shape.x1),
    y1: clampY(shape.y1)
  };
}

export default function ThermoHeightWizardModal({
  open,
  images,
  details,
  nasMountPath,
  onClose,
  onApply
}: Props) {
  const [candidateIds, setCandidateIds] = useState<number[]>([]);
  const [selectedSampleId, setSelectedSampleId] = useState<number | null>(null);
  const [heatmapData, setHeatmapData] = useState<ThermoPixel[]>([]);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapError, setHeatmapError] = useState<string | null>(null);
  const [drawMode, setDrawMode] = useState(false);
  const [shapes, setShapes] = useState<PlotShape[]>([]);
  const [heightInput, setHeightInput] = useState("");
  const [applyBusy, setApplyBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const candidateImages = useMemo(
    () => candidateIds.map((id) => images.find((image) => image.id === id)).filter(Boolean) as ThermoImage[],
    [candidateIds, images]
  );
  const selectedImage = selectedSampleId == null ? null : images.find((image) => image.id === selectedSampleId) || null;
  const selectedDetail = selectedSampleId == null ? null : details[selectedSampleId] || null;

  useEffect(() => {
    if (!open) return;
    const picked = shuffle(images.map((image) => image.id)).slice(0, Math.min(3, images.length));
    setCandidateIds(picked);
    setSelectedSampleId(null);
    setHeatmapData([]);
    setHeatmapError(null);
    setShapes([]);
    setHeightInput("");
    setDrawMode(false);
    setError(null);
    setApplyBusy(false);
  }, [open, images]);

  useEffect(() => {
    if (!open || selectedSampleId == null) return;
    setHeatmapLoading(true);
    setHeatmapError(null);
    fetchHeatmapData(selectedSampleId)
      .then((rows) => setHeatmapData(rows))
      .catch((err) => setHeatmapError(err instanceof Error ? err.message : "Heatmap load failed"))
      .finally(() => setHeatmapLoading(false));
  }, [open, selectedSampleId]);

  const { width, height, tempMatrix, tempMax, tempObservedMin } = useMemo(() => {
    if (!heatmapData.length) {
      return {
        width: 0,
        height: 0,
        tempMatrix: [] as number[][],
        tempMax: 600,
        tempObservedMin: 600
      };
    }
    let maxX = 0;
    let maxY = 0;
    let minTemperature = Number.POSITIVE_INFINITY;
    let maxTemperature = Number.NEGATIVE_INFINITY;
    heatmapData.forEach((point) => {
      if (point.x > maxX) maxX = point.x;
      if (point.y > maxY) maxY = point.y;
      minTemperature = Math.min(minTemperature, point.temperature);
      maxTemperature = Math.max(maxTemperature, point.temperature);
    });
    const matrix = Array.from({ length: maxY + 1 }, () => Array(maxX + 1).fill(NaN));
    heatmapData.forEach((point) => {
      matrix[point.y][point.x] = point.temperature;
    });
    return {
      width: maxX + 1,
      height: maxY + 1,
      tempMatrix: matrix,
      tempMax: Math.max(600, maxTemperature),
      tempObservedMin: minTemperature
    };
  }, [heatmapData]);

  const tempColorscale = useMemo(
    () => generateTempColorscale(tempMax || 1, 600),
    [tempMax]
  );

  const centerLineCoords = useMemo(() => {
    if (!selectedDetail?.centerLine?.length) return { x: [], y: [] };
    return {
      x: selectedDetail.centerLine.map((point) =>
        typeof point.x === "number" ? point.x : Math.floor(width / 2)
      ),
      y: selectedDetail.centerLine.map((point) => point.y)
    };
  }, [selectedDetail?.centerLine, width]);

  const displayLine = useMemo(() => {
    const extracted = extractLineShape(shapes);
    if (!extracted || !width || !height) return null;
    return clampShape(extracted, width, height);
  }, [height, shapes, width]);

  const plotConfig = useMemo(
    () => ({
      responsive: true,
      displaylogo: false,
      editable: true,
      modeBarButtonsToRemove: ["lasso2d", "select2d"],
      modeBarButtonsToAdd: ["drawline", "eraseshape"]
    }),
    []
  );

  const handleRelayout = (relayoutData: any) => {
    if (relayoutData?.dragmode) {
      setDrawMode(relayoutData.dragmode === "drawline");
    }
    if (Array.isArray(relayoutData?.shapes)) {
      setShapes(relayoutData.shapes as PlotShape[]);
      return;
    }
    const next = [...shapes];
    let changed = false;
    Object.entries(relayoutData || {}).forEach(([key, value]) => {
      const match = key.match(/^shapes\[(\d+)\]\.(.+)$/);
      if (!match) return;
      const index = Number(match[1]);
      if (value === null) {
        if (next[index]) {
          next[index] = undefined as never;
          changed = true;
        }
        return;
      }
      if (!next[index]) next[index] = {};
      (next[index] as Record<string, unknown>)[match[2]] = value;
      changed = true;
    });
    if (changed) {
      setShapes(next.filter(Boolean));
    }
  };

  const handleApply = async () => {
    if (!selectedSampleId || !displayLine) {
      setError("Select a reference sample and draw a reference line first.");
      return;
    }
    const referenceHeightMm = Number(heightInput);
    if (!Number.isFinite(referenceHeightMm) || referenceHeightMm <= 0) {
      setError("Enter a valid positive height for the selected line.");
      return;
    }
    setApplyBusy(true);
    setError(null);
    try {
      await onApply({
        sampleIds: images.map((image) => image.id),
        referenceSampleId: selectedSampleId,
        referenceLine: displayLine,
        referenceHeightMm
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to apply height reference.");
      setApplyBusy(false);
      return;
    }
    setApplyBusy(false);
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ width: "min(1100px, 94vw)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
          <div>
            <h3 style={{ margin: 0 }}>Multiple Analysis Height Wizard</h3>
            <p style={{ margin: "6px 0 0", color: "var(--text-subtle)" }}>
              Pick a reference sample, draw a known-height line, then apply a common height projection.
            </p>
          </div>
          <button className="button secondary" onClick={onClose} disabled={applyBusy}>
            Close
          </button>
        </div>

        {!selectedImage ? (
          <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
            <div className="label">Step 1 · Choose the best-fit reference sample</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${Math.max(1, candidateImages.length)}, minmax(0, 1fr))`,
                gap: 12
              }}
            >
              {candidateImages.map((image) => {
                const previewPath = buildTempImagePath(nasMountPath, image);
                return (
                  <div
                    key={image.id}
                    className="card"
                    style={{ background: "var(--surface-elevated)", display: "grid", gap: 10 }}
                  >
                    <div style={{ fontWeight: 600 }}>{image.filename}</div>
                    <div style={{ color: "var(--text-subtle)", fontSize: 12 }}>
                      #{image.id} · {image.cal_name} · {image.prod_step}
                    </div>
                    {previewPath ? (
                      <img
                        src={previewPath}
                        alt={image.filename}
                        style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: 8 }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          aspectRatio: "1 / 1",
                          display: "grid",
                          placeItems: "center",
                          background: "#0c1016",
                          border: "1px dashed #1e2b3d",
                          borderRadius: 8
                        }}
                      >
                        NAS not mounted
                      </div>
                    )}
                    <button className="button" onClick={() => setSelectedSampleId(image.id)}>
                      Use This Sample
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 16, display: "grid", gap: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div className="label">Step 2 · Draw the reference line and enter its height</div>
                <div style={{ color: "var(--text-subtle)", fontSize: 13, marginTop: 4 }}>
                  Sample #{selectedImage.id} · {selectedImage.filename}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="button secondary" onClick={() => setSelectedSampleId(null)} disabled={applyBusy}>
                  Pick Another Sample
                </button>
                <button className="button secondary" onClick={() => setDrawMode((value) => !value)} disabled={applyBusy}>
                  {drawMode ? "Drawing: On" : "Drawing: Off"}
                </button>
                <button className="button secondary" onClick={() => setShapes([])} disabled={applyBusy}>
                  Reset Line
                </button>
              </div>
            </div>

            <div
              className="grid"
              style={{ gridTemplateColumns: "minmax(0, 2fr) minmax(280px, 0.8fr)", gap: 14, alignItems: "start" }}
            >
              <div
                className="card"
                style={{ background: "var(--surface-elevated)", minHeight: 480, overflow: "hidden" }}
              >
                <div className="label">Temperature Heatmap</div>
                {heatmapLoading && <div style={{ paddingTop: 12 }}>Loading heatmap…</div>}
                {!heatmapLoading && heatmapError && (
                  <div className="status-error" style={{ marginTop: 12 }}>
                    {heatmapError}
                  </div>
                )}
                {!heatmapLoading && !heatmapError && width > 0 && (
                  <Plot
                    data={[
                      {
                        z: tempMatrix,
                        type: "heatmap",
                        colorscale: tempColorscale as any,
                        zmin: tempObservedMin,
                        zmax: tempMax,
                        colorbar: { title: "°C" }
                      },
                      {
                        x: centerLineCoords.x,
                        y: centerLineCoords.y,
                        mode: "lines",
                        line: { color: "#000000", width: 3 },
                        name: "Center line"
                      }
                    ]}
                    layout={{
                      autosize: true,
                      height: 420,
                      margin: { t: 10, r: 40, b: 10, l: 10 },
                      paper_bgcolor: "#0f1620",
                      plot_bgcolor: "#0f1620",
                      dragmode: drawMode ? "drawline" : "pan",
                      shapes: displayLine
                        ? [
                            {
                              type: "line",
                              x0: displayLine.x0,
                              y0: displayLine.y0,
                              x1: displayLine.x1,
                              y1: displayLine.y1,
                              line: { color: "#6be7c5", width: 3 }
                            }
                          ]
                        : shapes,
                      xaxis: {
                        showgrid: false,
                        showticklabels: false,
                        range: [0, Math.max(1, width - 1)],
                        fixedrange: true
                      },
                      yaxis: {
                        autorange: "reversed",
                        showgrid: false,
                        range: [height, 0],
                        scaleanchor: "x",
                        scaleratio: 1,
                        showticklabels: false,
                        fixedrange: true
                      }
                    }}
                    config={plotConfig}
                    style={{ width: "100%", height: "100%" }}
                    useResizeHandler
                    onRelayout={handleRelayout}
                  />
                )}
              </div>

              <div className="card" style={{ background: "var(--surface-elevated)", display: "grid", gap: 12 }}>
                <div>
                  <div className="label">Reference Height (mm)</div>
                  <input
                    className="input"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="Height in mm"
                    value={heightInput}
                    onChange={(event) => setHeightInput(event.target.value)}
                    disabled={applyBusy}
                  />
                </div>
                <div style={{ color: "var(--text-subtle)", fontSize: 13 }}>
                  Draw a line on the heatmap where the physical sample height is known, then enter
                  that height in millimeters.
                </div>
                <div className="pill">
                  Reference line: {displayLine ? "Ready" : "Not set"}
                </div>
                {selectedDetail ? (
                  <div className="pill">Center line points: {selectedDetail.centerLine.length}</div>
                ) : (
                  <div className="pill">Center line: loading…</div>
                )}
                {error && <div className="status-error">{error}</div>}
                <button
                  className="button"
                  onClick={handleApply}
                  disabled={applyBusy || !selectedDetail || heatmapLoading || !!heatmapError}
                >
                  {applyBusy ? "Applying…" : "Apply Height Projection"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
